import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import axios from 'axios';

const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';

// ============================================================================
// Types
// ============================================================================

interface Prediction {
  id: number;
  influencer_id?: number;
  expert_name?: string;
  twitter_handle?: string;
  price_prediction: string;
  prediction_type: string;
  success_status: string;
  start_date: string | null;
  end_date: string | null;
  target_time: string | null;
  prediction_made: string | null;
  source_url?: string;
  quote?: string;
  price_min?: number;
  price_max?: number;
  created_at: string;
  asset_type?: string;
  exclude_from_chart: boolean;
}

interface PipelineEntry {
  id: number;
  prediction_id: number;
  title: string;
  content: string;
  status: 'draft' | 'ready' | 'scheduled' | 'posted' | 'failed';
  screenshot_path?: string;
  scheduled_time?: string;
  posted_at?: string;
}

interface TimelinePrediction extends Prediction {
  dueDate: Date | null;
  daysUntilDue: number;
  isOverdue: boolean;
  isUpcoming: boolean;
  pipelineStatus: 'none' | 'draft' | 'ready' | 'scheduled' | 'posted' | 'failed';
  pipelineId?: number;
  hasScreenshot: boolean;
}

interface PredictionTimelineProps {
  theme: any;
}

// ============================================================================
// Animations
// ============================================================================

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 183, 77, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(255, 183, 77, 0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
`;

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  background: #0a0a0a;
  border-radius: 12px;
  padding: 24px;
  min-height: 600px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h2`
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatsBar = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const StatBadge = styled.div<{ color: string }>`
  background: ${props => props.color}22;
  border: 1px solid ${props => props.color}44;
  color: ${props => props.color};
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterButton = styled.button<{ active: boolean; color?: string }>`
  padding: 8px 16px;
  border: 1px solid ${props => props.active ? (props.color || '#f7931a') : '#333'};
  border-radius: 6px;
  background: ${props => props.active ? (props.color || '#f7931a') + '22' : '#111'};
  color: ${props => props.active ? (props.color || '#f7931a') : '#888'};
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.color || '#f7931a'};
    background: ${props => props.color || '#f7931a'}15;
  }
`;

const SearchInput = styled.input`
  padding: 10px 16px;
  background: #111;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  width: 250px;
  
  &:focus {
    outline: none;
    border-color: #f7931a;
  }
  
  &::placeholder {
    color: #666;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'success' | 'danger' | 'outline' }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return css`
          background: #f7931a;
          border: 1px solid #f7931a;
          color: #000;
          &:hover { background: #e8850f; }
        `;
      case 'success':
        return css`
          background: #22c55e22;
          border: 1px solid #22c55e;
          color: #22c55e;
          &:hover { background: #22c55e33; }
        `;
      case 'danger':
        return css`
          background: #ef444422;
          border: 1px solid #ef4444;
          color: #ef4444;
          &:hover { background: #ef444433; }
        `;
      default:
        return css`
          background: transparent;
          border: 1px solid #555;
          color: #aaa;
          &:hover { border-color: #888; color: #fff; }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DateHeader = styled.div<{ isToday?: boolean; isPast?: boolean }>`
  background: ${props => props.isToday ? '#f7931a22' : props.isPast ? '#ef444411' : '#111'};
  border-left: 3px solid ${props => props.isToday ? '#f7931a' : props.isPast ? '#ef4444' : '#333'};
  padding: 10px 16px;
  color: ${props => props.isToday ? '#f7931a' : props.isPast ? '#ef4444' : '#888'};
  font-weight: 600;
  font-size: 14px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:first-child {
    margin-top: 0;
  }
`;

const PredictionCard = styled.div<{ isHighlighted?: boolean; status?: string }>`
  background: #111;
  border: 1px solid ${props => props.isHighlighted ? '#f7931a' : '#222'};
  border-radius: 8px;
  padding: 16px;
  margin-left: 20px;
  animation: ${slideIn} 0.3s ease;
  transition: all 0.2s;
  
  ${props => props.isHighlighted && css`
    animation: ${pulse} 2s infinite;
    border-color: #f7931a;
    background: #f7931a08;
  `}
  
  &:hover {
    border-color: #444;
    background: #151515;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const InfluencerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f7931a, #e8850f);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #000;
  font-size: 16px;
`;

const InfluencerName = styled.div`
  font-weight: 600;
  color: #fff;
  font-size: 15px;
`;

const Handle = styled.div`
  color: #666;
  font-size: 13px;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.status) {
      case 'success':
        return css`
          background: #22c55e22;
          color: #22c55e;
          border: 1px solid #22c55e44;
        `;
      case 'miss':
        return css`
          background: #ef444422;
          color: #ef4444;
          border: 1px solid #ef444444;
        `;
      default:
        return css`
          background: #f59e0b22;
          color: #f59e0b;
          border: 1px solid #f59e0b44;
        `;
    }
  }}
`;

const PredictionDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
`;

const DetailItem = styled.div`
  background: #0a0a0a;
  padding: 10px 12px;
  border-radius: 6px;
`;

const DetailLabel = styled.div`
  color: #666;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const DetailValue = styled.div`
  color: #fff;
  font-size: 15px;
  font-weight: 600;
`;

const QuoteText = styled.div`
  background: #0a0a0a;
  padding: 12px;
  border-radius: 6px;
  border-left: 2px solid #f7931a;
  color: #aaa;
  font-size: 13px;
  font-style: italic;
  margin-bottom: 12px;
  max-height: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 12px;
  border-top: 1px solid #222;
`;

const PipelineIndicator = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${props => {
    switch (props.status) {
      case 'posted': return '#22c55e';
      case 'ready':
      case 'scheduled': return '#3b82f6';
      case 'draft': return '#f59e0b';
      case 'failed': return '#ef4444';
      default: return '#666';
    }
  }};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #222;
  border-radius: 2px;
  margin: 12px 0;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ percent: number; color: string }>`
  height: 100%;
  width: ${props => props.percent}%;
  background: ${props => props.color};
  transition: width 0.3s ease;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  z-index: 10;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #333;
  border-top-color: #f7931a;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  
  h3 {
    color: #888;
    margin-bottom: 8px;
  }
`;

const ExpandedContent = styled.div<{ expanded: boolean }>`
  max-height: ${props => props.expanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const TweetPreview = styled.div`
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 16px;
  margin-top: 12px;
`;

const TweetText = styled.textarea`
  width: 100%;
  min-height: 100px;
  background: #111;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  padding: 12px;
  font-size: 14px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #f7931a;
  }
`;

const ScreenshotPreview = styled.div`
  margin-top: 12px;
  
  img {
    width: 100%;
    max-width: 400px;
    border-radius: 8px;
    border: 1px solid #333;
  }
`;

// ============================================================================
// Component
// ============================================================================

const PredictionTimeline: React.FC<PredictionTimelineProps> = ({ theme }) => {
  const [predictions, setPredictions] = useState<TimelinePrediction[]>([]);
  const [pipelineEntries, setPipelineEntries] = useState<PipelineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [generatedTweets, setGeneratedTweets] = useState<Record<number, string>>({});
  
  // Ref for scrolling to next prediction
  const nextPredictionRef = React.useRef<HTMLDivElement>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'success' | 'miss'>('all');
  const [pipelineFilter, setPipelineFilter] = useState<'all' | 'needs-post' | 'has-draft' | 'posted'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [predRes, pipelineRes] = await Promise.all([
        axios.get(`${apiBase}/api/admin/predictions/all`),
        axios.get(`${apiBase}/api/tweet-pipeline`)
      ]);
      
      // Handle both array and { predictions: [...] } response formats
      const pipelineData: PipelineEntry[] = Array.isArray(pipelineRes.data) 
        ? pipelineRes.data 
        : (pipelineRes.data?.items || pipelineRes.data?.tweets || []);
      setPipelineEntries(pipelineData);
      
      // Create pipeline lookup
      const pipelineByPrediction: Record<number, PipelineEntry> = {};
      pipelineData.forEach(entry => {
        if (entry.prediction_id && (!pipelineByPrediction[entry.prediction_id] || 
            entry.status === 'posted' || entry.status === 'ready')) {
          pipelineByPrediction[entry.prediction_id] = entry;
        }
      });
      
      // Handle both array and { predictions: [...] } response formats
      const predictionsArray = Array.isArray(predRes.data) 
        ? predRes.data 
        : (predRes.data?.predictions || []);
      
      // Process predictions with timeline data
      const now = new Date();
      const processedPredictions: TimelinePrediction[] = predictionsArray.map((p: Prediction) => {
        const dueDate = p.target_time ? new Date(p.target_time) : 
                        p.end_date ? new Date(p.end_date) : null;
        
        const daysUntilDue = dueDate ? 
          Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : 999;
        
        const pipelineEntry = pipelineByPrediction[p.id];
        
        return {
          ...p,
          dueDate,
          daysUntilDue,
          isOverdue: daysUntilDue < 0,
          isUpcoming: daysUntilDue >= 0 && daysUntilDue <= 7,
          pipelineStatus: pipelineEntry?.status || 'none',
          pipelineId: pipelineEntry?.id,
          hasScreenshot: !!pipelineEntry?.screenshot_path
        };
      });
      
      setPredictions(processedPredictions);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Filter and sort predictions
  const filteredPredictions = useMemo(() => {
    let result = predictions;
    
    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(p => p.success_status === statusFilter);
    }
    
    // Pipeline filter
    if (pipelineFilter === 'needs-post') {
      result = result.filter(p => p.pipelineStatus === 'none' || p.pipelineStatus === 'draft');
    } else if (pipelineFilter === 'has-draft') {
      result = result.filter(p => ['draft', 'ready', 'scheduled'].includes(p.pipelineStatus));
    } else if (pipelineFilter === 'posted') {
      result = result.filter(p => p.pipelineStatus === 'posted');
    }
    
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => 
        (p.expert_name?.toLowerCase().includes(q)) ||
        (p.twitter_handle?.toLowerCase().includes(q)) ||
        (p.quote?.toLowerCase().includes(q)) ||
        p.id.toString().includes(q)
      );
    }
    
    // Sort by due date (upcoming first, then overdue, then future)
    return result.sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate.getTime() - b.dueDate.getTime();
    });
  }, [predictions, statusFilter, pipelineFilter, searchQuery]);
  
  // Group by date
  const groupedPredictions = useMemo(() => {
    const groups: Record<string, TimelinePrediction[]> = {};
    
    filteredPredictions.forEach(p => {
      const dateKey = p.dueDate ? p.dueDate.toISOString().split('T')[0] : 'no-date';
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(p);
    });
    
    return groups;
  }, [filteredPredictions]);
  
  // Find the NEXT prediction that needs attention (first unposted by due date)
  const nextPredictionId = useMemo(() => {
    // Sort all predictions by due date
    const sorted = [...predictions]
      .filter(p => p.dueDate)
      .sort((a, b) => a.dueDate!.getTime() - b.dueDate!.getTime());
    
    // Find first one that needs a post (no tweet or only draft)
    const needsPost = sorted.find(p => 
      p.pipelineStatus === 'none' || p.pipelineStatus === 'draft'
    );
    
    return needsPost?.id || null;
  }, [predictions]);
  
  // Stats
  const stats = useMemo(() => {
    const needsPost = predictions.filter(p => 
      (p.pipelineStatus === 'none' || p.pipelineStatus === 'draft')
    ).length;
    
    const overdueCount = predictions.filter(p => 
      p.isOverdue && (p.pipelineStatus === 'none' || p.pipelineStatus === 'draft')
    ).length;
    const upcomingCount = predictions.filter(p => p.isUpcoming && p.success_status === 'pending').length;
    const postedCount = predictions.filter(p => p.pipelineStatus === 'posted').length;
    
    return { needsPost, overdueCount, upcomingCount, postedCount, total: predictions.length };
  }, [predictions]);
  
  // Scroll to next prediction
  const scrollToNext = () => {
    if (nextPredictionRef.current) {
      nextPredictionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  
  // Generate AI tweet and screenshot
  const generateTweetAndScreenshot = async (predictionId: number) => {
    setProcessingIds(prev => new Set(prev).add(predictionId));
    
    try {
      // Generate AI tweet
      const tweetRes = await axios.post(`${apiBase}/api/admin/generate-ai-tweet`, {
        prediction_id: predictionId,
        post_type: 'resolution'
      });
      
      const generatedTweet = tweetRes.data.tweet || tweetRes.data.content;
      setGeneratedTweets(prev => ({ ...prev, [predictionId]: generatedTweet }));
      
      // Generate screenshot
      await axios.post(`${apiBase}/api/admin/predictions/${predictionId}/screenshot`);
      
      // Create pipeline entry
      await axios.post(`${apiBase}/api/tweet-pipeline`, {
        prediction_id: predictionId,
        content: generatedTweet,
        title: `Prediction #${predictionId} Resolution`,
        status: 'draft',
        topic: 'prediction_resolution'
      });
      
      // Reload data
      await loadData();
      
      // Expand the card to show the tweet
      setExpandedIds(prev => new Set(prev).add(predictionId));
      
    } catch (error) {
      console.error('Failed to generate tweet:', error);
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(predictionId);
        return newSet;
      });
    }
  };
  
  // Set pipeline entry to ready
  const setReady = async (predictionId: number, pipelineId?: number) => {
    if (!pipelineId) return;
    
    try {
      await axios.put(`${apiBase}/api/tweet-pipeline/${pipelineId}`, {
        status: 'ready'
      });
      await loadData();
    } catch (error) {
      console.error('Failed to set ready:', error);
    }
  };
  
  // Post now
  const postNow = async (pipelineId: number) => {
    try {
      // This would trigger actual posting via X.com API
      await axios.post(`${apiBase}/api/tweet-pipeline/${pipelineId}/post`);
      await loadData();
    } catch (error) {
      console.error('Failed to post:', error);
    }
  };
  
  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };
  
  // Format price
  const formatPrice = (p: TimelinePrediction) => {
    if (p.price_min && p.price_max) {
      return `$${p.price_min.toLocaleString()} - $${p.price_max.toLocaleString()}`;
    }
    return `$${parseInt(p.price_prediction).toLocaleString()}`;
  };
  
  // Get pipeline status icon
  const getPipelineIcon = (status: string) => {
    switch (status) {
      case 'posted': return '✅';
      case 'ready': return '🟢';
      case 'scheduled': return '📅';
      case 'draft': return '📝';
      case 'failed': return '❌';
      default: return '⚪';
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      </Container>
    );
  }
  
  return (
    <Container>
      <Header>
        <Title>
          📅 Prediction Timeline
        </Title>
        <StatsBar>
          {nextPredictionId && (
            <ActionButton variant="primary" onClick={scrollToNext}>
              ⬇️ Jump to Next
            </ActionButton>
          )}
          <StatBadge color="#ef4444">
            🔴 {stats.overdueCount} overdue
          </StatBadge>
          <StatBadge color="#f59e0b">
            ⏳ {stats.needsPost} need posts
          </StatBadge>
          <StatBadge color="#22c55e">
            ✅ {stats.postedCount} posted
          </StatBadge>
          <StatBadge color="#666">
            📊 {stats.total} total
          </StatBadge>
        </StatsBar>
      </Header>
      
      <FilterRow>
        <FilterButton 
          active={statusFilter === 'all'} 
          onClick={() => setStatusFilter('all')}
        >
          All Status
        </FilterButton>
        <FilterButton 
          active={statusFilter === 'pending'} 
          color="#f59e0b"
          onClick={() => setStatusFilter('pending')}
        >
          Pending
        </FilterButton>
        <FilterButton 
          active={statusFilter === 'success'} 
          color="#22c55e"
          onClick={() => setStatusFilter('success')}
        >
          Success
        </FilterButton>
        <FilterButton 
          active={statusFilter === 'miss'} 
          color="#ef4444"
          onClick={() => setStatusFilter('miss')}
        >
          Miss
        </FilterButton>
        
        <div style={{ width: 1, height: 24, background: '#333', margin: '0 8px' }} />
        
        <FilterButton 
          active={pipelineFilter === 'all'} 
          onClick={() => setPipelineFilter('all')}
        >
          All Pipeline
        </FilterButton>
        <FilterButton 
          active={pipelineFilter === 'needs-post'} 
          color="#ef4444"
          onClick={() => setPipelineFilter('needs-post')}
        >
          Needs Post
        </FilterButton>
        <FilterButton 
          active={pipelineFilter === 'has-draft'} 
          color="#3b82f6"
          onClick={() => setPipelineFilter('has-draft')}
        >
          Has Draft
        </FilterButton>
        <FilterButton 
          active={pipelineFilter === 'posted'} 
          color="#22c55e"
          onClick={() => setPipelineFilter('posted')}
        >
          Posted
        </FilterButton>
        
        <div style={{ flex: 1 }} />
        
        <SearchInput 
          placeholder="Search by name, handle, or ID..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        
        <ActionButton variant="outline" onClick={loadData}>
          🔄 Refresh
        </ActionButton>
      </FilterRow>
      
      {filteredPredictions.length === 0 ? (
        <EmptyState>
          <h3>No predictions found</h3>
          <p>Try adjusting your filters</p>
        </EmptyState>
      ) : (
        <Timeline>
          {Object.entries(groupedPredictions).map(([dateKey, preds]) => {
            const isToday = dateKey === new Date().toISOString().split('T')[0];
            const isPast = dateKey !== 'no-date' && new Date(dateKey) < new Date();
            
            return (
              <React.Fragment key={dateKey}>
                <DateHeader isToday={isToday} isPast={isPast}>
                  {dateKey === 'no-date' ? '📌 No Due Date' : (
                    <>
                      {isPast ? '⚠️' : isToday ? '🔥' : '📅'} 
                      {formatDate(dateKey)}
                      <span style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.7 }}>
                        {preds.length} prediction{preds.length !== 1 ? 's' : ''}
                      </span>
                    </>
                  )}
                </DateHeader>
                
                {preds.map(pred => {
                  const isProcessing = processingIds.has(pred.id);
                  const isExpanded = expandedIds.has(pred.id);
                  const isNextPrediction = pred.id === nextPredictionId;
                  
                  return (
                    <PredictionCard 
                      key={pred.id}
                      ref={isNextPrediction ? nextPredictionRef : undefined}
                      isHighlighted={isNextPrediction}
                      status={pred.success_status}
                    >
                      {isProcessing && (
                        <LoadingOverlay>
                          <Spinner />
                        </LoadingOverlay>
                      )}
                      
                      <CardHeader>
                        <InfluencerInfo>
                          <Avatar>
                            {(pred.expert_name || pred.twitter_handle || '?')[0].toUpperCase()}
                          </Avatar>
                          <div>
                            <InfluencerName>
                              {pred.expert_name || 'Unknown'}
                            </InfluencerName>
                            <Handle>
                              @{pred.twitter_handle || 'unknown'} • #{pred.id}
                            </Handle>
                          </div>
                        </InfluencerInfo>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <PipelineIndicator status={pred.pipelineStatus}>
                            {getPipelineIcon(pred.pipelineStatus)}
                            {pred.pipelineStatus === 'none' ? 'No tweet' : pred.pipelineStatus}
                          </PipelineIndicator>
                          <StatusBadge status={pred.success_status}>
                            {pred.success_status}
                          </StatusBadge>
                        </div>
                      </CardHeader>
                      
                      <PredictionDetails>
                        <DetailItem>
                          <DetailLabel>Prediction</DetailLabel>
                          <DetailValue>{formatPrice(pred)}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Type</DetailLabel>
                          <DetailValue>{pred.prediction_type}</DetailValue>
                        </DetailItem>
                      </PredictionDetails>
                      
                      {pred.quote && (
                        <QuoteText>
                          "{pred.quote.substring(0, 150)}..."
                        </QuoteText>
                      )}
                      
                      <CardActions>
                        {pred.pipelineStatus === 'none' && (
                          <ActionButton 
                            variant="primary"
                            onClick={() => generateTweetAndScreenshot(pred.id)}
                            disabled={isProcessing}
                          >
                            ✨ Generate Tweet + Screenshot
                          </ActionButton>
                        )}
                        
                        {pred.pipelineStatus === 'draft' && (
                          <>
                            <ActionButton 
                              variant="success"
                              onClick={() => setReady(pred.id, pred.pipelineId)}
                            >
                              ✅ Set Ready
                            </ActionButton>
                            <ActionButton 
                              variant="outline"
                              onClick={() => setExpandedIds(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(pred.id)) newSet.delete(pred.id);
                                else newSet.add(pred.id);
                                return newSet;
                              })}
                            >
                              {isExpanded ? '▲ Hide' : '▼ Edit Tweet'}
                            </ActionButton>
                          </>
                        )}
                        
                        {pred.pipelineStatus === 'ready' && pred.pipelineId && (
                          <ActionButton 
                            variant="primary"
                            onClick={() => postNow(pred.pipelineId!)}
                          >
                            🚀 Post Now
                          </ActionButton>
                        )}
                        
                        {pred.pipelineStatus === 'posted' && (
                          <ActionButton variant="outline" disabled>
                            ✅ Posted
                          </ActionButton>
                        )}
                        
                        {pred.source_url && (
                          <ActionButton 
                            variant="outline"
                            onClick={() => window.open(pred.source_url, '_blank')}
                          >
                            🔗 Source
                          </ActionButton>
                        )}
                      </CardActions>
                      
                      <ExpandedContent expanded={isExpanded}>
                        <TweetPreview>
                          <DetailLabel>Tweet Content</DetailLabel>
                          <TweetText
                            value={generatedTweets[pred.id] || ''}
                            onChange={e => setGeneratedTweets(prev => ({
                              ...prev,
                              [pred.id]: e.target.value
                            }))}
                            placeholder="Tweet content will appear here..."
                          />
                          {pred.hasScreenshot && (
                            <ScreenshotPreview>
                              <DetailLabel>Screenshot</DetailLabel>
                              <img 
                                src={`${apiBase}/chart-screenshots/chart_prediction_${pred.id}_latest.png`}
                                alt="Chart screenshot"
                                onError={(e) => (e.target as HTMLImageElement).style.display = 'none'}
                              />
                            </ScreenshotPreview>
                          )}
                        </TweetPreview>
                      </ExpandedContent>
                    </PredictionCard>
                  );
                })}
              </React.Fragment>
            );
          })}
        </Timeline>
      )}
    </Container>
  );
};

export default PredictionTimeline;
